/**
 * Filter Panel Component
 *
 * Provides advanced filtering UI for list views.
 * Supports multiple filters with field, operator, and value selection.
 */

import React, { useState } from 'react';
import { X, Plus, Filter as FilterIcon } from 'lucide-react';

export interface FilterField {
  fieldApiName: string;
  fieldLabel: string;
  fieldType: 'text' | 'number' | 'date' | 'datetime' | 'picklist' | 'boolean';
  picklistValues?: string[];
}

export interface FilterCondition {
  field: string;
  operator: string;
  value: any;
}

interface FilterPanelProps {
  fields: FilterField[];
  activeFilters: Record<string, FilterCondition>;
  onApply: (filters: Record<string, FilterCondition>) => void;
  onClose: () => void;
}

const OPERATORS_BY_TYPE: Record<string, Array<{ value: string; label: string }>> = {
  text: [
    { value: 'contains', label: 'Contains' },
    { value: 'equals', label: 'Equals' },
    { value: 'startsWith', label: 'Starts With' },
    { value: 'endsWith', label: 'Ends With' },
    { value: 'notContains', label: 'Does Not Contain' },
    { value: 'notEquals', label: 'Does Not Equal' },
  ],
  number: [
    { value: 'equals', label: 'Equals' },
    { value: 'notEquals', label: 'Does Not Equal' },
    { value: 'greaterThan', label: 'Greater Than' },
    { value: 'lessThan', label: 'Less Than' },
    { value: 'greaterOrEqual', label: 'Greater or Equal' },
    { value: 'lessOrEqual', label: 'Less or Equal' },
  ],
  date: [
    { value: 'equals', label: 'Equals' },
    { value: 'notEquals', label: 'Does Not Equal' },
    { value: 'after', label: 'After' },
    { value: 'before', label: 'Before' },
    { value: 'between', label: 'Between' },
  ],
  datetime: [
    { value: 'equals', label: 'Equals' },
    { value: 'notEquals', label: 'Does Not Equal' },
    { value: 'after', label: 'After' },
    { value: 'before', label: 'Before' },
    { value: 'between', label: 'Between' },
  ],
  picklist: [
    { value: 'equals', label: 'Equals' },
    { value: 'notEquals', label: 'Does Not Equal' },
    { value: 'in', label: 'In' },
    { value: 'notIn', label: 'Not In' },
  ],
  boolean: [
    { value: 'equals', label: 'Equals' },
  ],
};

export const FilterPanel: React.FC<FilterPanelProps> = ({
  fields,
  activeFilters,
  onApply,
  onClose,
}) => {
  const [filters, setFilters] = useState<Record<string, FilterCondition>>(activeFilters);
  const [newFilterField, setNewFilterField] = useState<string>('');

  // Add a new filter
  const handleAddFilter = () => {
    if (!newFilterField) return;

    const field = fields.find(f => f.fieldApiName === newFilterField);
    if (!field) return;

    const operators = OPERATORS_BY_TYPE[field.fieldType];
    const defaultOperator = operators[0].value;

    setFilters({
      ...filters,
      [newFilterField]: {
        field: newFilterField,
        operator: defaultOperator,
        value: '',
      },
    });
    setNewFilterField('');
  };

  // Remove a filter
  const handleRemoveFilter = (fieldApiName: string) => {
    const newFilters = { ...filters };
    delete newFilters[fieldApiName];
    setFilters(newFilters);
  };

  // Update filter operator
  const handleOperatorChange = (fieldApiName: string, operator: string) => {
    setFilters({
      ...filters,
      [fieldApiName]: {
        ...filters[fieldApiName],
        operator,
      },
    });
  };

  // Update filter value
  const handleValueChange = (fieldApiName: string, value: any) => {
    setFilters({
      ...filters,
      [fieldApiName]: {
        ...filters[fieldApiName],
        value,
      },
    });
  };

  // Apply filters
  const handleApply = () => {
    // Filter out empty values
    const validFilters = Object.entries(filters).reduce((acc, [key, filter]) => {
      if (filter.value !== '' && filter.value !== null && filter.value !== undefined) {
        acc[key] = filter;
      }
      return acc;
    }, {} as Record<string, FilterCondition>);

    onApply(validFilters);
  };

  // Clear all filters
  const handleClearAll = () => {
    setFilters({});
  };

  // Render value input based on field type
  const renderValueInput = (fieldApiName: string, field: FilterField, filter: FilterCondition) => {
    switch (field.fieldType) {
      case 'text':
        return (
          <input
            type="text"
            value={filter.value || ''}
            onChange={(e) => handleValueChange(fieldApiName, e.target.value)}
            placeholder="Enter value..."
            className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-[#1e293b] text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-transparent"
          />
        );

      case 'number':
        return (
          <input
            type="number"
            value={filter.value || ''}
            onChange={(e) => handleValueChange(fieldApiName, parseFloat(e.target.value) || '')}
            placeholder="Enter number..."
            className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-[#1e293b] text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-transparent"
          />
        );

      case 'date':
        return (
          <input
            type="date"
            value={filter.value || ''}
            onChange={(e) => handleValueChange(fieldApiName, e.target.value)}
            className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-[#1e293b] text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-transparent"
          />
        );

      case 'datetime':
        return (
          <input
            type="datetime-local"
            value={filter.value || ''}
            onChange={(e) => handleValueChange(fieldApiName, e.target.value)}
            className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-[#1e293b] text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-transparent"
          />
        );

      case 'picklist':
        return (
          <select
            value={filter.value || ''}
            onChange={(e) => handleValueChange(fieldApiName, e.target.value)}
            className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-[#1e293b] text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-transparent"
          >
            <option value="">Select value...</option>
            {field.picklistValues?.map((value) => (
              <option key={value} value={value}>
                {value}
              </option>
            ))}
          </select>
        );

      case 'boolean':
        return (
          <select
            value={filter.value || ''}
            onChange={(e) => handleValueChange(fieldApiName, e.target.value === 'true')}
            className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-[#1e293b] text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-transparent"
          >
            <option value="">Select value...</option>
            <option value="true">Yes</option>
            <option value="false">No</option>
          </select>
        );

      default:
        return null;
    }
  };

  // Get available fields (not yet used in filters)
  const availableFields = fields.filter(f => !filters[f.fieldApiName]);

  return (
    <div className="fixed inset-0 bg-black/50 dark:bg-black/70 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-[#1e293b] rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-100 dark:bg-blue-500/20 rounded-lg flex items-center justify-center">
              <FilterIcon className="w-5 h-5 text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                Filter Records
              </h2>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Add filters to narrow down your search
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 dark:hover:bg-[#334155] rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-gray-500 dark:text-gray-400" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          {/* Active Filters */}
          {Object.entries(filters).map(([fieldApiName, filter]) => {
            const field = fields.find(f => f.fieldApiName === fieldApiName);
            if (!field) return null;

            const operators = OPERATORS_BY_TYPE[field.fieldType];

            return (
              <div
                key={fieldApiName}
                className="flex items-start gap-3 p-4 bg-gray-50 dark:bg-[#0f172a] rounded-lg border border-gray-200 dark:border-gray-700"
              >
                {/* Field Name */}
                <div className="flex-1 min-w-0">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {field.fieldLabel}
                  </label>
                  <div className="flex gap-2">
                    {/* Operator */}
                    <select
                      value={filter.operator}
                      onChange={(e) => handleOperatorChange(fieldApiName, e.target.value)}
                      className="w-40 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-[#1e293b] text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-transparent"
                    >
                      {operators.map((op) => (
                        <option key={op.value} value={op.value}>
                          {op.label}
                        </option>
                      ))}
                    </select>

                    {/* Value */}
                    {renderValueInput(fieldApiName, field, filter)}
                  </div>
                </div>

                {/* Remove Button */}
                <button
                  onClick={() => handleRemoveFilter(fieldApiName)}
                  className="p-2 text-gray-400 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-500/20 rounded-lg transition-colors"
                  title="Remove filter"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            );
          })}

          {/* Add New Filter */}
          {availableFields.length > 0 && (
            <div className="flex items-end gap-3 p-4 bg-blue-50 dark:bg-blue-500/10 rounded-lg border border-blue-200 dark:border-blue-500/30">
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Add Filter
                </label>
                <select
                  value={newFilterField}
                  onChange={(e) => setNewFilterField(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-[#1e293b] text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-transparent"
                >
                  <option value="">Select field...</option>
                  {availableFields.map((field) => (
                    <option key={field.fieldApiName} value={field.fieldApiName}>
                      {field.fieldLabel}
                    </option>
                  ))}
                </select>
              </div>
              <button
                onClick={handleAddFilter}
                disabled={!newFilterField}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 dark:disabled:bg-gray-700 text-white rounded-lg transition-colors flex items-center gap-2 disabled:cursor-not-allowed"
              >
                <Plus className="w-4 h-4" />
                Add
              </button>
            </div>
          )}

          {/* No Filters Message */}
          {Object.keys(filters).length === 0 && (
            <div className="text-center py-8">
              <FilterIcon className="w-12 h-12 text-gray-400 dark:text-gray-500 mx-auto mb-3" />
              <p className="text-gray-600 dark:text-gray-400">No filters applied</p>
              <p className="text-sm text-gray-500 dark:text-gray-500">
                Select a field above to add your first filter
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-[#0f172a]">
          <button
            onClick={handleClearAll}
            disabled={Object.keys(filters).length === 0}
            className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-[#334155] rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Clear All
          </button>
          <div className="flex items-center gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-[#334155] rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleApply}
              className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors flex items-center gap-2"
            >
              <FilterIcon className="w-4 h-4" />
              Apply Filters
              {Object.keys(filters).length > 0 && (
                <span className="ml-1 px-2 py-0.5 bg-blue-500 text-white text-xs rounded-full">
                  {Object.keys(filters).length}
                </span>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
